import { ref } from 'vue'
import { defineStore } from 'pinia'
import type {
  ArgQueryGovernmentSettings,
  FeatureOptions
} from '@/types/api/governmentSetting/governmentSetting'
import MiiaFwConfig from '@/configs/config'
import { queryGovernmentSetting } from '@/api/governmentSetting/governmentSettingDaoApiGwFuncs'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { GlobalError } from '@/utils/globalError/globalError'
import { FEATURE_OPTIONS } from '@/constants/api/governmentSetting/featureOptions'

export const useGovernmentSettingStore = defineStore(
  'governmentSettingStore',
  () => {
    const initialMapLocation = ref<number[] | undefined>(undefined)
    const upperLimitQuantityOfInspection = ref<number | undefined>(undefined)
    const serviceLimitEnable = ref<boolean | undefined>(undefined)
    const manualUrl = ref<string | undefined>(undefined)
    const isDeviceAuthentication = ref<boolean | undefined>(undefined)
    const featureOptions = ref<FeatureOptions[]>([])

    /**
     * State をまとめて更新
     */
    const updateState = (obj: {
      initialMapLocation: number[] | undefined
      upperLimitQuantityOfInspection: number | undefined
      serviceLimitEnable: boolean | undefined
      manualUrl: string | undefined
      isDeviceAuthentication: boolean | undefined
      featureOptions: FeatureOptions[]
    }) => {
      initialMapLocation.value = obj.initialMapLocation
      upperLimitQuantityOfInspection.value = obj.upperLimitQuantityOfInspection
      serviceLimitEnable.value = obj.serviceLimitEnable
      manualUrl.value = obj.manualUrl
      isDeviceAuthentication.value = obj.isDeviceAuthentication
      featureOptions.value = obj.featureOptions
    }

    /**
     * storeの状態をリセットする
     */
    const reset = () => {
      initialMapLocation.value = undefined
      upperLimitQuantityOfInspection.value = undefined
      serviceLimitEnable.value = undefined
      manualUrl.value = undefined
      isDeviceAuthentication.value = undefined
      featureOptions.value = []
    }

    /**
     * 自治体設定APIを呼び出し、値をセットする
     * @param param {govCode 自治体コード, viewCode 表示コード}
     */
    const fetchAndSetGovernmentSetting = async (param: {
      govCode: string | undefined
      viewCode?: string
    }) => {
      try {
        // パラメータが未定義、または自治体コードが未設定の場合は早期リターン
        if (param === undefined || param.govCode === undefined || param.govCode === '') return

        const governmentSettingQueryParams: ArgQueryGovernmentSettings = {
          system: MiiaFwConfig.config.sysId,
          govCode: param.govCode
        }

        const res = await queryGovernmentSetting(governmentSettingQueryParams)

        if (res !== undefined) {
          const governmentSettingInfo = {
            initialMapLocation: res.initialMapLocation,
            featureOptions: res.featureOptions ?? [],
            upperLimitQuantityOfInspection: res.upperLimitQuantityOfInspection,
            serviceLimitEnable: res.serviceLimitEnable,
            manualUrl: res.manualUrl,
            isDeviceAuthentication: res.isDeviceAuthentication
          }
          updateState(governmentSettingInfo)
          return governmentSettingInfo
        } else {
          throw new GlobalError([ERROR_CODES.GOVERNMENT_SETTING_FETCH_FAILED])
        }
      } catch (err) {
        console.error(err)
        throw new GlobalError([ERROR_CODES.GOVERNMENT_SETTING_FETCH_FAILED])
      }
    }

    /**
     * 機能オプションに`ScreeningAuth`が含まれているか
     * @returns true:含まれている
     *          false:含まれていない
     */
    const includesFeatureOptionsScreening = () => {
      return featureOptions.value.includes(FEATURE_OPTIONS.screeningAuth)
    }

    /**
     * 機能オプションに`EmergencyAuth`が含まれているか
     * @returns true:含まれている、false:含まれていない
     */
    const includesFeatureOptionsEmergency = () => {
      return featureOptions.value.includes(FEATURE_OPTIONS.emergencyAuth)
    }

    return {
      initialMapLocation,
      upperLimitQuantityOfInspection,
      serviceLimitEnable,
      manualUrl,
      isDeviceAuthentication,
      featureOptions,
      updateState,
      reset,
      fetchAndSetGovernmentSetting,
      includesFeatureOptionsScreening,
      includesFeatureOptionsEmergency
    }
  },
  {
    persist: true
  }
)
